import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

import Colors from "../../Resources/Color";
import { useDevice } from "../_layout";

const WorkoutsDashboardTabBar = memo(function WorkoutsDashboardTabBar({
  selectedKey = "workouts",
  onSelect,
  workoutsLabel,
  dashboardLabel,
  itemGap = 12,
  topMargin = 0,
  activeColor = Colors.bubbleColor,
  inactiveColor = Colors.blackColor,
}) {
  const { isMobile } = useDevice();

  const [hoveredKey, setHoveredKey] = useState(null);
  const [tabLayouts, setTabLayouts] = useState({});
  const [labelWidths, setLabelWidths] = useState({});
  const [layoutEpoch, setLayoutEpoch] = useState(0);

  const tabs = useMemo(
    () => [
      { key: "workouts", label: workoutsLabel },
      { key: "dashboard", label: dashboardLabel },
    ],
    [dashboardLabel, workoutsLabel],
  );
  const resolvedSelectedKey = selectedKey === "dashboard" ? "dashboard" : "workouts";
  const tabsMeasurementSignature = useMemo(
    () => tabs.map((tab) => `${tab.key}:${String(tab.label)}`).join("||"),
    [tabs],
  );

  const selectedLayout = tabLayouts[resolvedSelectedKey];
  const selectedLabelWidth = labelWidths[resolvedSelectedKey];

  const indicatorTarget = useMemo(() => {
    if (!selectedLayout) return null;

    const width =
      typeof selectedLabelWidth === "number" && selectedLabelWidth > 0
        ? selectedLabelWidth
        : selectedLayout.width;

    return {
      x: selectedLayout.x + (selectedLayout.width - width) / 2,
      width,
    };
  }, [selectedLabelWidth, selectedLayout]);

  const resetMeasurements = useCallback(() => {
    setHoveredKey(null);
    setTabLayouts({});
    setLabelWidths({});
    setLayoutEpoch((previous) => previous + 1);
  }, []);

  const updateTabLayout = useCallback((key, { nativeEvent }) => {
    const { x, width } = nativeEvent.layout;

    setTabLayouts((previous) => {
      const current = previous[key];
      if (current && current.x === x && current.width === width) {
        return previous;
      }

      return {
        ...previous,
        [key]: { x, width },
      };
    });
  }, []);

  const updateLabelWidth = useCallback((key, { nativeEvent }) => {
    const width = nativeEvent.layout?.width;
    if (typeof width !== "number" || width <= 0) return;

    setLabelWidths((previous) => {
      if (previous[key] === width) return previous;

      return {
        ...previous,
        [key]: width,
      };
    });
  }, []);

  const handleSelect = useCallback(
    (key) => {
      if (!key || key === resolvedSelectedKey) return;
      onSelect?.(key);
    },
    [onSelect, resolvedSelectedKey],
  );

  useEffect(() => {
    resetMeasurements();
  }, [resetMeasurements, tabsMeasurementSignature]);

  return (
    <>
      <View style={{ marginTop: topMargin }}>
        <View style={styles.row}>
          {tabs.map((tab, index) => {
            const isActive = resolvedSelectedKey === tab.key;
            const isHovered = hoveredKey === tab.key;
            const tabRenderKey = `${layoutEpoch}:${tab.key}:${String(tab.label)}`;

            return (
              <View
                key={tabRenderKey}
                onLayout={(event) => updateTabLayout(tab.key, event)}
                style={[
                  styles.tabItem,
                  { marginRight: index < tabs.length - 1 ? itemGap : 0 },
                ]}
              >
                <TouchableOpacity
                  activeOpacity={0.75}
                  onPress={() => handleSelect(tab.key)}
                  onMouseEnter={!isMobile ? () => setHoveredKey(tab.key) : undefined}
                  onMouseLeave={!isMobile ? () => setHoveredKey(null) : undefined}
                >
                  <View style={styles.tabContent}>
                    <Text
                      numberOfLines={1}
                      onLayout={(event) => updateLabelWidth(tab.key, event)}
                      style={[
                        styles.tabText,
                        { color: isActive || isHovered ? activeColor : inactiveColor },
                      ]}
                    >
                      {tab.label}
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            );
          })}
          {indicatorTarget ? (
            <View
              pointerEvents="none"
              style={[
                styles.activeUnderline,
                {
                  left: indicatorTarget.x,
                  width: indicatorTarget.width,
                  backgroundColor: activeColor,
                },
              ]}
            />
          ) : null}
        </View>
      </View>
    </>
  );
});

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "center",
    position: "relative",
    paddingBottom: 0,
  },
  tabItem: {
    justifyContent: "center",
    paddingBottom: 7,
    flexShrink: 1,
  },
  tabContent: {
    alignItems: "center",
  },
  tabText: {
    fontSize: 14,
    fontWeight: "400",
  },
  activeUnderline: {
    position: "absolute",
    bottom: 0,
    left: 0,
    height: 2,
    borderRadius: 1,
  },
});

export default WorkoutsDashboardTabBar;
